import { NavLink } from "react-router-dom";
import { NavIcon, type NavIconName } from "./NavIcon";

/**
 * G-9.2 kit: HIG-style sidebar row — rounded icon tile + label, recessed
 * hover, accent-soft selection with a filled-accent tile. Replaces the old
 * bare-text NavLinks (owner: "just text as buttons… generic and lazy").
 * Styles live in ui.css (.nav-item*).
 */
export function NavItem({
  to,
  icon,
  label,
  end = false,
  badge,
  badges,
}: {
  to: string;
  icon: NavIconName;
  label: string;
  end?: boolean;
  badge?: number;
  /**
   * G-160 (OD-263): several counts side by side, each with its tone and what
   * it counts (read out by screen readers) — red "blocked", orange "soon".
   */
  badges?: Array<{ count: number; tone: "danger" | "warning"; label: string }>;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => (isActive ? "nav-item is-active" : "nav-item")}
    >
      <span className="nav-item-icon">
        <NavIcon name={icon} />
      </span>
      <span className="nav-item-label">{label}</span>
      {badge != null && badge > 0 ? (
        <span className="nav-item-badge" aria-label={`${badge} pending`}>
          {badge}
        </span>
      ) : null}
      {badges
        ?.filter((b) => b.count > 0)
        .map((b) => (
          <span
            key={b.tone}
            className={b.tone === "warning" ? "nav-item-badge is-warning" : "nav-item-badge"}
            aria-label={`${b.count} ${b.label}`}
          >
            {b.count}
          </span>
        ))}
    </NavLink>
  );
}

/** Indented child row (e.g. a shop under Locations) — dot marker, no tile. */
export function NavSubItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        isActive ? "nav-subitem is-active" : "nav-subitem"
      }
    >
      <span className="nav-subitem-dot" aria-hidden="true" />
      <span className="nav-item-label">{label}</span>
    </NavLink>
  );
}
